import { FC, useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Params } from "react-router-dom";
import { Button, FontAwesomeIcon, Tooltip } from "@inceptionbg/ui-components";
import { DateInput } from "@inceptionbg/ui-components";
import {
  formatCurrency,
  ITableColumn,
  ITableDataItem,
  ITableFilter,
} from "@inceptionbg/ui-components";
import { FarmContext } from "../../AppContent";
import {
  getRecapitulation,
  downloadRecapitulationExcel,
  downloadRecapitulationPdf,
} from "../../repos/FIeldBookRepo";
import { SelectAsyncPaginate } from "../../Components/Inputs/SelectAsyncPaginate";
import { getLandCultureOptions } from "../../utils/LoadOptions";
import { Table } from "../../Components/CustomIUI/Table/Table";
import { deleteProps } from "@inceptionbg/ui-components";
import {
  IFieldEntryRecapitulationData,
  IFieldEntryRecapitulationFilters,
} from "../../types/IFieldBook";
import { faCircleInfo } from "../../assets/icons/solid/faCircleInfo";

export const ProfitLossReportPage: FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [recapitulationData, setRecapitulationData] =
    useState<IFieldEntryRecapitulationData | null>(null);
  const [filters, setFilters] = useState<IFieldEntryRecapitulationFilters>({});
  const [appliedFilters, setAppliedFilters] =
    useState<IFieldEntryRecapitulationFilters>({});
  const [selectedPlantCulture, setSelectedPlantCulture] = useState<{
    value: string;
    label: string;
  } | null>(null);

  const { t } = useTranslation();
  const farmUuid = useContext(FarmContext).farm?.uuid;
  const { uuid } = useParams<Params>() as Params;

  const handleGetRecapitulation = useCallback(
    (filters: IFieldEntryRecapitulationFilters) => {
      if (!uuid) return;
      setIsLoading(true);
      getRecapitulation({ ...filters, fieldUuid: uuid })
        .then((data) => {
          setRecapitulationData(data);
        })
        .catch(() => {})
        .finally(() => setIsLoading(false));
    },
    [uuid]
  );

  useEffect(() => {
    if (uuid && farmUuid) {
      setFilters({});
      setAppliedFilters({});
      handleGetRecapitulation({});
    }
  }, [uuid, farmUuid, handleGetRecapitulation]);

  const onSearch = useCallback(
    (search: IFieldEntryRecapitulationFilters) => {
      setAppliedFilters(search);
      if (!search.plantCultureUuid) {
        setSelectedPlantCulture(null);
      }
      handleGetRecapitulation(search);
    },
    [handleGetRecapitulation]
  );

  const recapitulationFilters: (
    search: IFieldEntryRecapitulationFilters,
    setSearch: (search: IFieldEntryRecapitulationFilters) => void
  ) => ITableFilter = (search, setSearch) => ({
    plantCulture: {
      label: t("PlantCultureC"),
      field: (
        <div className="flex center full-width gap-2">
          <Tooltip
            className="field-tooltip"
            label={t("ReportFiltersTooltip")}
            position="top"
          >
            <FontAwesomeIcon color="#00A6ED" icon={faCircleInfo} />
          </Tooltip>
          <SelectAsyncPaginate
            value={selectedPlantCulture}
            loadOptions={(
              searchText: string,
              loadedOptions: [],
              offset: { page: number }
            ) =>
              getLandCultureOptions(searchText, loadedOptions, offset, {
                fieldUuid: uuid,
              })
            }
            onChange={(e) => {
              setSelectedPlantCulture(e);
              setSearch(
                e
                  ? { ...search, plantCultureUuid: e.value }
                  : deleteProps(search, ["plantCultureUuid"])
              );
            }}
            placeholder={t("ChoosePlaceholder")}
          />
        </div>
      ),
      resetField: () => {
        setSelectedPlantCulture(null);
        setSearch(deleteProps(search, ["plantCultureUuid"]));
      },
    },
    period: {
      label: `${t("Period")} ${t("from")}`,
      field: (
        <div className="flex-center full-width">
          <Tooltip
            className="field-tooltip"
            label={t("ReportPeriodTooltip")}
            position="top"
          >
            <FontAwesomeIcon
              className="mr-2"
              color="#00A6ED"
              icon={faCircleInfo}
            />
          </Tooltip>
          <DateInput
            date={search.periodFrom || ""}
            setDate={(date) =>
              setSearch(
                date
                  ? { ...search, periodFrom: date }
                  : deleteProps(search, ["periodFrom"])
              )
            }
          />
          <p className="mx-2">{t("to")}</p>
          <DateInput
            date={search.periodTo || ""}
            setDate={(date) =>
              setSearch(
                date
                  ? { ...search, periodTo: date }
                  : deleteProps(search, ["periodTo"])
              )
            }
          />
        </div>
      ),
      resetField: () =>
        setSearch(deleteProps(search, ["periodFrom", "periodTo"])),
    },
  });

  const handleDownloadExcel = useCallback(() => {
    if (!uuid) return;
    setIsLoading(true);
    downloadRecapitulationExcel({ ...appliedFilters, fieldUuid: uuid })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [appliedFilters, uuid]);

  const handleDownloadPdf = useCallback(() => {
    if (!uuid) return;
    setIsLoading(true);
    downloadRecapitulationPdf({ ...appliedFilters, fieldUuid: uuid })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [appliedFilters, uuid]);

  const columns: ITableColumn[] = [
    { id: "plantCultureName", label: t("PlantCultureC") },
    { id: "income", label: t("Income"), align: "right" },
    { id: "expenses", label: t("Expenses"), align: "right" },
    { id: "netProfit", label: t("NetProfit"), align: "right" },
  ];

  const tableData: ITableDataItem[] = recapitulationData
    ? recapitulationData.items?.map((item, index) => ({
        uuid: item.plantCulture?.uuid || `item-${index}`,
        cells: {
          plantCultureName: {
            value: item.plantCulture
              ? `${item.plantCulture.code} - ${item.plantCulture.name}`
              : "-",
          },
          income: {
            value: formatCurrency(item.income || 0),
            // className: (item.income || 0) >= 0 ? "" : "text-error",
          },
          expenses: {
            value: formatCurrency(item.expense || 0),
            // className: (item.expense || 0) >= 0 ? "" : "text-error",
          },
          netProfit: {
            value: formatCurrency(item.profit || 0),
            // className: (item.profit || 0) >= 0 ? "text-success" : "text-error",
          },
        },
      })) || []
    : [];

  const totalsData: ITableDataItem | undefined = recapitulationData
    ? {
        uuid: "totals",
        cells: {
          plantCultureName: { value: t("Total") },
          income: {
            value: formatCurrency(recapitulationData.totalIncome),
            // className: recapitulationData.totalIncome >= 0 ? "" : "text-error",
          },
          expenses: {
            value: formatCurrency(recapitulationData.totalExpenses),
            // className:
            //   recapitulationData.totalExpenses >= 0 ? "" : "text-error",
          },
          netProfit: {
            value: formatCurrency(recapitulationData.netProfit),
            // className:
            //   recapitulationData.netProfit >= 0 ? "text-success" : "text-error",
          },
        },
      }
    : undefined;

  return (
    <div>
      <div className="page-section">
        <h1>
          {t("ProfitLossReport")}
          {recapitulationData?.items?.[0]?.field?.name &&
            ` - ${recapitulationData.items[0].field.name}`}
        </h1>
      </div>

      <div className="sub-section-content">
        <Table
          columns={columns}
          data={tableData}
          totals={totalsData}
          isLoading={isLoading}
          hideFooter
          filterData={{
            columns: ["plantCulture", "period"],
            filters: recapitulationFilters(filters, setFilters),
            search: appliedFilters,
            searchData: filters,
            setSearchData: setFilters,
            onSubmit: onSearch,
          }}
        />

        <div className="flex gap-2 justify-right mt-3">
          <Button
            label={t("ExportExcel")}
            size="s"
            outlined
            onClick={handleDownloadExcel}
            disabled={!recapitulationData}
          />
          <Button
            label={t("ExportPdf")}
            size="s"
            outlined
            onClick={handleDownloadPdf}
            disabled={!recapitulationData}
          />
        </div>
      </div>
    </div>
  );
};
